import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Guard from "components/access-control/rbac/Guard";
import { PRIV } from "components/access-control/rbac/privileges";
import Unauthorised from "components/access-control/rbac/Unauthorised";
import { usePermissions } from "components/access-control/rbac/usePermissions";
import AdvanceTable from "components/common/advance-table/AdvanceTable";
import AdvanceTableFooter from "components/common/advance-table/AdvanceTableFooter";
import Avatar from "components/common/Avatar";
import CopyableText from "components/common/CopyableText";
import SubtleBadge from "components/common/SubtleBadge";
import { useAdvanceTable } from "hooks";
import useCredits from "hooks/donations/useCredits";
import useDonationsList from "hooks/donations/useDonationsList";
import useFormattedDate from "hooks/useFormatDate";
import AdvanceTableProvider from "providers/AdvanceTableProvider";
import React, { useEffect, useState } from "react";
import { Button, Card, Col, Row, Spinner } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";

export const DonationDetails = () => {
  const { donationId } = useParams();
  const { can } = usePermissions();
  const navigate = useNavigate();
  const [donation, setDonation] = useState(null);

  const { loading, fetchDonationById, error } = useDonationsList();
  const { credits } = useCredits();
  const filteredCredits = credits?.filter(
    (credit) => credit?.donation?.id === donationId
  );

  const { formatDateTime } = useFormattedDate();

  useEffect(() => {
    const fetchDonation = async () => {
      if (!donationId) return;
      try {
        const donation = await fetchDonationById(donationId);
        setDonation(donation);
      } catch (err) {
        setDonation(null);
      }
    };
    fetchDonation();
  }, [donationId]);

  const formatID = (id) => `${id.slice(0, 12)}...`;
  const formatText = (text) => `${text.slice(0, 30)}...`;

  // purpose columns
  const columns = [
    {
      accessorKey: "id",
      header: "ID",
      cell: ({ getValue }) => formatID(getValue()),
      meta: {
        headerProps: { className: "text-900" },
      },
    },
    {
      accessorKey: "code",
      header: "Code",
      meta: {
        headerProps: { className: "text-900" },
      },
    },
    {
      accessorKey: "name",
      header: "Name",
      meta: {
        headerProps: { className: "text-900" },
      },
    },
    {
      accessorKey: "active",
      header: "Status",
      cell: ({ getValue }) => {
        const value = getValue();
        const type = value == true ? "success" : "secondary";
        const label = value == true ? "Active" : "Disabled";

        return (
          <SubtleBadge pill bg={type}>
            {label}
          </SubtleBadge>
        );
      },
      meta: {
        headerProps: { className: "text-900" },
        cellProps: { className: "fs-9" },
      },
    },
    {
      accessorKey: "description",
      header: "Description",
      cell: ({ getValue }) => formatText(getValue()),
      meta: {
        headerProps: { className: "text-900" },
      },
    },
  ];

  // issued credits columns
  const creditColumns = [
    {
      accessorKey: "code",
      header: "Code",
      meta: {
        headerProps: { className: "text-900" },
      },
    },
    {
      accessorKey: "donee",
      header: "Donee",
      cell: ({ getValue }) => {
        const donee = getValue();
        if (!donee) return "NULL";

        // Merge firstName + lastName
        const fullName = `${donee.firstName || ""} ${
          donee.lastName || ""
        }`.trim();
        return fullName || donee.nickName || "NULL";
      },
      meta: {
        headerProps: { className: "text-900" },
      },
    },
    {
      accessorKey: "amount",
      header: "Amount",
      cell: ({ getValue }) =>
        getValue() ? `$${getValue().toFixed(2)}` : "$0.00",
      meta: {
        headerProps: { className: "text-900" },
      },
    },
    {
      accessorKey: "balance",
      header: "Balance",
      cell: ({ getValue }) =>
        getValue() ? `$${getValue().toFixed(2)}` : "$0.00",
      meta: {
        headerProps: { className: "text-900" },
      },
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ getValue }) => {
        const value = getValue();
        const statusMap = {
          ACTIVE: { type: "success", label: "Active" },
          REDEEMED: { type: "info", label: "Redeemed" },
          EXPIRED: { type: "danger", label: "Expired" },
          DELETED: { type: "danger", label: "Deleted" },
        };

        const { type, label } = statusMap[value];
        return (
          <SubtleBadge pill bg={type}>
            {label}
          </SubtleBadge>
        );
      },
      meta: {
        headerProps: { className: "text-900" },
        cellProps: { className: "fs-9" },
      },
    },
    {
      accessorKey: "issuedAt",
      header: "Issued At",
      cell: ({ getValue }) => {
        return formatDateTime(getValue());
      },
      meta: {
        headerProps: { className: "text-900" },
      },
    },
  ];

  const table = useAdvanceTable({
    data: donation?.purposes || [],
    columns,
    sortable: true,
    pagination: true,
    perPage: 10,
  });

  const creditsTable = useAdvanceTable({
    data: filteredCredits || [],
    columns: creditColumns,
    sortable: true,
    pagination: true,
    perPage: 10,
  });

  const statusMap = {
    ACTIVE: { type: "success", label: "Active" },
    PENDING: { type: "warning", label: "Pending" },
    EXPIRED: { type: "danger", label: "Expired" },
    DELETED: { type: "danger", label: "Deleted" },
  };

  const { type, label } = statusMap[donation?.status] || {
    type: "secondary",
    label: "Unknown",
  };

  if (loading && !donation) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: 400 }}
      >
        <Spinner animation="border" variant="success" />
        <span className="ms-3" style={{ fontSize: "13.33px" }}>
          Loading...
        </span>
      </div>
    );
  }

  return (
    <>
      <Card className="mb-4 shadow-none mt-3 rounded border-1">
        <Card.Body className="d-flex align-items-center py-3 px-3 px-lg-4 flex-wrap">
          {/* Left side: Back button */}
          <div className="d-flex align-items-center" style={{ minWidth: 90 }}>
            <Button
              type="button"
              onClick={() => navigate("/olitask/donations/list")}
              size="sm"
              aria-label="Go back"
              variant="outline-success"
            >
              <span style={{ fontSize: "1.3em", lineHeight: 1 }}>←</span> Back
            </Button>
          </div>

          {/* Center: Title */}
          <div className="px-3">
            <h4 className="mb-0 fw-semibold text-base">Donation details</h4>
          </div>

          {/* Right side: Action buttons */}
          <div className="flex-1 d-flex mt-3 mt-sm-0 align-items-center justify-content-sm-end gap-2">
            <Guard privilege={PRIV.ISSUE_CREDIT}>
              <Button
                variant="success"
                size="sm"
                onClick={() =>
                  navigate(`/olitask/donations/${donationId}/issue-credit`)
                }
                className="text-nowrap text-xs"
                disabled={donation?.balance === 0 || !donation?.balance}
              >
                Issue Credit
                <FontAwesomeIcon
                  icon="check"
                  transform="shrink-2"
                  className="ms-1"
                />
              </Button>
            </Guard>
          </div>
        </Card.Body>
      </Card>

      <Row className="g-3">
        {/* donor info */}
        <Col lg={4} className="mb-3">
          <Card>
            <Card.Header>
              <h6 className="mb-0 fw-bold text-center ps-2dd">
                Donor Information
              </h6>
            </Card.Header>
            <Card.Body className="py-0">
              {/* Profile Photo */}
              <div className="d-flex align-items-center justify-content-center ps-2dd">
                {donation?.donor?.profilePhoto ? (
                  <Avatar
                    src={donation?.donor?.profilePhoto}
                    size="4xl"
                    className="mb-3"
                  />
                ) : (
                  <Avatar
                    size="4xl"
                    name={
                      `${donation?.donor?.firstName || ""} ${
                        donation?.donor?.lastName || ""
                      }`.trim() ||
                      donation?.donor?.nickName ||
                      "OB"
                    }
                    className="mb-3"
                  />
                )}
              </div>

              <div className="mb-3 ps-2">
                <div className="mb-1">
                  <label className="form-label text-xxs mb-0">Full name</label>
                  <h6 className="mb-0 fw-semibold text-sm">
                    {`${donation?.donor?.firstName || ""} ${
                      donation?.donor?.lastName || ""
                    }`.trim() ||
                      donation?.donor?.nickName ||
                      "Not provided"}
                  </h6>
                </div>
                <div className="mb-1">
                  <label className="form-label text-xxs mb-0">Email</label>
                  <h6 className="mb-0 fw-semibold text-sm">
                    {donation?.donor?.email || "Not provided"}
                  </h6>
                </div>
                <div className="mb-2">
                  <label className="form-label text-xxs mb-0">Phone</label>
                  <h6 className="mb-0 fw-semibold text-sm">
                    {donation?.donor?.phone || "Not provided"}
                  </h6>
                </div>
                <div className="mb-2">
                  <label className="form-label text-xxs mb-0">Gender</label>
                  <h6 className="mb-0 fw-semibold text-sm">
                    {donation?.donor?.gender || "Not provided"}
                  </h6>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* donee info */}
        {donation?.doneeUser && (
          <Col lg={4} className="mb-3">
            <Card>
              <Card.Header>
                <h6 className="mb-0 fw-bold text-center">Donee Information</h6>
              </Card.Header>
              <Card.Body className="py-0">
                {/* Profile Photo */}
                <div className="d-flex align-items-center justify-content-center">
                  {donation?.doneeUser?.profilePhoto ? (
                    <Avatar
                      src={donation?.doneeUser?.profilePhoto}
                      size="4xl"
                      className="mb-3"
                    />
                  ) : (
                    <Avatar
                      size="4xl"
                      name={
                        `${donation?.doneeUser?.firstName || ""} ${
                          donation?.doneeUser?.lastName || ""
                        }`.trim() || donation?.donee?.nickName
                      }
                      className="mb-3"
                    />
                  )}
                </div>

                <div className="mb-3 ps-2">
                  <div className="mb-1">
                    <label className="form-label text-xxs mb-0">
                      Full name
                    </label>
                    <h6 className="mb-0 fw-semibold text-sm">
                      {`${donation?.doneeUser?.firstName || ""} ${
                        donation?.doneeUser?.lastName || ""
                      }`.trim() || donation?.doneeUser.nickName}
                    </h6>
                  </div>
                  <div className="mb-1">
                    <label className="form-label text-xxs mb-0">Email</label>
                    <h6 className="mb-0 fw-semibold text-sm">
                      {donation?.doneeUser?.email || "Not provided"}
                    </h6>
                  </div>
                  <div className="mb-2">
                    <label className="form-label text-xxs mb-0">Phone</label>
                    <h6 className="mb-0 fw-semibold text-sm">
                      {donation?.doneeUser?.phone || "Not provided"}
                    </h6>
                  </div>
                  <div className="">
                    <label className="form-label text-xxs mb-0">Gender</label>
                    <h6 className="mb-0 fw-semibold text-sm">
                      {donation?.doneeUser?.gender || "Not provided"}
                    </h6>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        )}

        {/* additional information */}
        <Col lg={donation?.doneeUser ? 12 : 8} className="mb-3">
          <Card>
            <Card.Header>
              <h6 className="mb-0 fw-bold ps-2">Additional Information</h6>
            </Card.Header>
            <Card.Body className="py-0 pb-2">
              <Row>
                <Col>
                  <div className="mb-3 ps-2">
                    <div className="mb-2">
                      <label className="form-label text-xxs mb-0">
                        Donation ID
                      </label>
                      <h6 className="mb-0 fw-semibold text-sm">
                        <CopyableText value={donation?.id} />
                      </h6>
                    </div>
                    <div className="mb-2">
                      <label className="form-label text-xxs mb-0">Code</label>
                      <h6 className="mb-0 fw-semibold text-sm">
                        {donation?.code}
                      </h6>
                    </div>
                    <div className="mb-2">
                      <label className="form-label text-xxs mb-0">Amount</label>
                      <h6 className="mb-0 fw-semibold text-sm">
                        $
                        {donation?.amount
                          ? donation?.amount.toFixed(2)
                          : "0.00"}
                      </h6>
                    </div>
                    <div className="mb-2">
                      <label className="form-label text-xxs mb-0">
                        Balance
                      </label>
                      <h6 className="mb-0 fw-semibold text-sm">
                        $
                        {donation?.balance
                          ? donation?.balance.toFixed(2)
                          : "0.00"}
                      </h6>
                    </div>
                    <div className="mb-2">
                      <label className="form-label text-xxs mb-0">
                        Currency
                      </label>
                      <h6 className="mb-0 fw-semibold text-sm">
                        {donation?.currency}
                      </h6>
                    </div>
                    <div className="mb-2">
                      <label className="form-label text-xxs mb-0">
                        Transaction ID
                      </label>
                      <h6 className="mb-0 fw-semibold text-sm">
                        <CopyableText
                          value={donation?.transactionId}
                          displayValue={donation?.transactionId || "Not provided"}
                        />
                      </h6>
                    </div>
                  </div>
                </Col>
                <Col>
                  <div className="mb-3 ps-2">
                    <div className="mb-2">
                      <label className="form-label text-xxs mb-0 d-block">
                        Status
                      </label>
                      <SubtleBadge pill bg={type}>
                        {label}
                      </SubtleBadge>
                    </div>
                    <div className="mb-2">
                      <label className="form-label text-xxs mb-0">
                        Created At
                      </label>
                      <h6 className="mb-0 fw-semibold text-sm">
                        {formatDateTime(donation?.createdAt)}
                      </h6>
                    </div>
                    <div className="mb-2">
                      <label className="form-label text-xxs mb-0">
                        Updated At
                      </label>
                      <h6 className="mb-0 fw-semibold text-sm">
                        {formatDateTime(donation?.updatedAt) || "N/A"}
                      </h6>
                    </div>

                    <div className="mb-2">
                      <label className="form-label text-xxs mb-0">
                        Short Note
                      </label>
                      <h6 className="mb-0 fw-semibold text-sm">
                        {donation?.note || "Not provided"}
                      </h6>
                    </div>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>

        {/* purposes Information */}
        <Col lg={12}>
          <h6 className="fw-bold text-sm">Donation purposes</h6>
          {donation?.purposes && donation?.purposes.length > 0 ? (
            <AdvanceTableProvider {...table}>
              <AdvanceTable
                headerClassName="bg-200 text-nowrap align-middle"
                rowClassName="btn-reveal-trigger text-nowrap align-middle"
                tableProps={{
                  bordered: true,
                  striped: true,
                  className: "fs-10 mb-0 overflow-hidden",
                }}
              />
            </AdvanceTableProvider>
          ) : (
            <div className="text-xs">
              No purposes found assigned to this donation
            </div>
          )}
        </Col>
      </Row>

      <Col lg={12} className="mt-4">
        <h6 className="fw-bold text-sm">Issued credits</h6>
        {filteredCredits && filteredCredits.length > 0 ? (
          <AdvanceTableProvider {...creditsTable}>
            <AdvanceTable
              headerClassName="bg-200 text-nowrap align-middle"
              rowClassName="btn-reveal-trigger text-nowrap align-middle"
              tableProps={{
                bordered: true,
                striped: true,
                className: "fs-10 mb-0 overflow-hidden",
              }}
            />
            <div className="mt-3">
              <AdvanceTableFooter rowInfo navButtons rowsPerPageSelection />
            </div>
          </AdvanceTableProvider>
        ) : (
          <div className="text-xs">No credits issued for this donation.</div>
        )}
      </Col>
    </>
  );
};
